import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLogin } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/auth';

const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_state_invalid: 'SSO session expired. Please try again.',
  sso_provider_mismatch: 'SSO provider mismatch. Please try again.',
  sso_no_account: 'No account found for this email. Please register first or contact your administrator.',
  sso_failed: 'SSO authentication failed. Please try again or use email/password.',
};

const API_BASE = import.meta.env.VITE_API_URL || '';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [ssoProviders, setSsoProviders] = useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const successMessage = searchParams.get('message');
  const ssoError = searchParams.get('error');

  const navigate = useNavigate();
  const loginMutation = useLogin();
  const { logout } = useAuthStore();

  // Clear any existing session when visiting login page
  useEffect(() => {
    logout();
  }, [logout]);

  // Fetch available SSO providers
  useEffect(() => {
    fetch(`${API_BASE}/api/v1/auth/sso/providers`)
      .then((r) => r.json())
      .then((data) => setSsoProviders(data.providers || []))
      .catch((err) => {
        console.warn('Failed to fetch SSO providers:', err);
        setSsoProviders([]);
      });
  }, []);

  // Show SSO error from redirect
  useEffect(() => {
    if (ssoError && SSO_ERROR_MESSAGES[ssoError]) {
      setError(SSO_ERROR_MESSAGES[ssoError]);
    }
  }, [ssoError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('EMAIL REQUIRED');
      return;
    }

    if (!password) {
      setError('PASSWORD REQUIRED');
      return;
    }

    try {
      await loginMutation.mutateAsync({ email, password });
      navigate('/dashboard', { replace: true });
    } catch {
      setError('INVALID CREDENTIALS');
    }
  };

  const hasSso = ssoProviders.length > 0;

  return (
    <div className="min-h-screen bg-mono-700 flex items-center justify-center py-12 px-4 relative overflow-hidden">
      {/* Background ARGOS logo — zoomed-in left side, matching the landing page sections */}
      <img
        src="/argos-full-black.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute top-[58%] left-[-5vw] -translate-y-1/2 h-[250vw] md:h-[400vh] w-auto max-w-none object-contain opacity-30 select-none [filter:drop-shadow(0_18px_40px_rgba(0,0,0,0.55))]"
      />
      {/* Dim overhead spotlight — radial gradient simulates light from above */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_45%_at_50%_25%,_rgba(255,255,255,0.10)_0%,_rgba(255,255,255,0.03)_40%,_transparent_70%)]"
      />
      <div className="absolute top-0 left-0 right-0 z-20 pointer-events-none">
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <Link
            to="/"
            className="pointer-events-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-mono-300 text-xs tracking-[0.3em] font-orbitron hover:border-white/20 hover:bg-white/[0.06] transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            HOME
          </Link>
        </div>
      </div>
      <div className="relative z-10 max-w-sm w-full">
        {successMessage && (
          <div className="border border-green-500/30 bg-green-500/10 p-3 text-center mb-6">
            <p className="text-xs text-green-400 tracking-wider">{successMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="border border-red-500/30 bg-red-500/10 p-3 text-center">
              <p className="text-xs text-red-400 tracking-wider">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-[10px] text-mono-400 tracking-[0.3em] mb-2 font-orbitron">
              EMAIL
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-lg text-mono-100 text-sm tracking-wide placeholder:text-mono-500/60 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] focus:border-cyan-400/50 focus:bg-white/[0.06] focus:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_0_0_3px_rgba(34,211,238,0.12),0_0_24px_-6px_rgba(34,211,238,0.4)] focus:outline-none transition-all duration-200"
              placeholder="you@company.com"
              autoFocus
              autoComplete="email"
            />
          </div>

          <div>
            <label className="block text-[10px] text-mono-400 tracking-[0.3em] mb-2 font-orbitron">
              PASSWORD
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-lg text-mono-100 text-sm tracking-wide placeholder:text-mono-500/60 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] focus:border-cyan-400/50 focus:bg-white/[0.06] focus:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_0_0_3px_rgba(34,211,238,0.12),0_0_24px_-6px_rgba(34,211,238,0.4)] focus:outline-none transition-all duration-200"
              placeholder="Enter password"
              autoComplete="current-password"
            />
          </div>

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-[10px] text-cyan-400/80 hover:text-cyan-300 tracking-[0.2em] transition-colors"
            >
              FORGOT PASSWORD?
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="group relative w-full py-3.5 rounded-lg overflow-hidden border border-cyan-400/40 bg-gradient-to-r from-cyan-500/90 via-cyan-400/90 to-cyan-500/90 text-black text-xs font-semibold tracking-[0.35em] font-orbitron shadow-[inset_0_1px_0_0_rgba(255,255,255,0.3),0_0_28px_-6px_rgba(34,211,238,0.55)] hover:from-cyan-400 hover:via-cyan-300 hover:to-cyan-400 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.4),0_0_36px_-4px_rgba(34,211,238,0.75)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <span className="relative z-10">
                {loginMutation.isPending ? 'AUTHENTICATING...' : 'SIGN IN'}
              </span>
              {/* Subtle inner highlight sheen */}
              <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
            </button>
          </div>
        </form>

        {/* SSO Buttons */}
        {hasSso && (
          <div className="mt-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-px bg-mono-800" />
              <span className="text-[10px] text-mono-500 tracking-widest">OR</span>
              <div className="flex-1 h-px bg-mono-800" />
            </div>

            <div className="space-y-3">
              {ssoProviders.includes('google') && (
                <a
                  href={`${API_BASE}/api/v1/auth/sso/google`}
                  className="w-full py-3 px-4 border border-mono-700 bg-white text-mono-900 text-xs font-medium tracking-widest hover:bg-mono-100 transition-colors flex items-center justify-center gap-3"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  SIGN IN WITH GOOGLE
                </a>
              )}

              {ssoProviders.includes('microsoft') && (
                <a
                  href={`${API_BASE}/api/v1/auth/sso/microsoft`}
                  className="w-full py-3 px-4 border border-mono-700 bg-[#2F2F2F] text-white text-xs font-medium tracking-widest hover:bg-[#3F3F3F] transition-colors flex items-center justify-center gap-3"
                >
                  <svg viewBox="0 0 21 21" className="w-4 h-4" aria-hidden="true">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                  </svg>
                  SIGN IN WITH MICROSOFT
                </a>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-[10px] text-mono-500 tracking-wider">
            DON'T HAVE AN ACCOUNT?{' '}
            <Link to="/contact-sales" className="text-cyan-400 hover:text-cyan-300">
              CONTACT US
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
